import { platformApiFetch } from '@/services/platformApi/client';
import { ProfileData } from '@/types/supabase';
import { getSupabaseErrorMessage } from '@/utils/supabase/errorMessages';

/**
 * Fetch the current authenticated user's profile.
 * Throws on error so callers can surface appropriately.
 */
export async function fetchCurrentUserProfile(userId: string): Promise<ProfileData | null> {
  if (!userId) return null;
  return platformApiFetch<ProfileData | null>(`/v1/profiles/${userId}`);
}

/**
 * Update a user's profile data
 */
export async function updateUserProfile(userId: string, updates: Partial<ProfileData>): Promise<void> {
  try {
    await platformApiFetch(`/v1/profiles/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  } catch (error) {
    throw new Error(getSupabaseErrorMessage(error, 'Failed to update profile'));
  }
}

/**
 * Fetch user profile by email
 */
export async function fetchUserProfileByEmail(email: string): Promise<{ first_name?: string; last_name?: string } | null> {
  try {
    return await platformApiFetch<{ first_name?: string; last_name?: string } | null>('/v1/profiles/by-email', {
      method: 'POST',
      body: JSON.stringify({ email, fields: 'first_name, last_name' }),
    });
  } catch (error) {
    console.error('Error fetching user profile by email:', error);
    return null;
  }
}

/**
 * Fetch user profile by email with specific fields
 */
export async function fetchUserProfileByEmailWithFields<T = any>(
  email: string, 
  fields: string
): Promise<T | null> {
  try {
    return await platformApiFetch<T | null>('/v1/profiles/by-email', {
      method: 'POST',
      body: JSON.stringify({ email, fields }),
    });
  } catch (error) {
    console.error('Error fetching user profile by email:', error);
    return null;
  }
}

/**
 * Get all profiles (admin only)
 */
export async function getAllProfiles() {
  return platformApiFetch('/v1/admin/profiles');
}

/**
 * Get WhatsApp verification status for a user
 */
export async function getUserWhatsAppVerificationStatus(userId: string): Promise<boolean> {
  try {
    return await platformApiFetch<boolean>(`/v1/profiles/${userId}/whatsapp-status`);
  } catch (error) {
    console.error("Error checking WhatsApp verification status:", error);
    return false;
  }
}

/**
 * Fetch all user profiles for admin purposes
 */
export async function getAllUserProfiles(): Promise<Array<{ 
  id: string; 
  first_name: string; 
  last_name: string; 
  email: string; 
  whatsapp_number: string | null 
}>> {
  const data = await platformApiFetch<Array<{ id: string; first_name: string; last_name: string; email: string; whatsapp_number: string | null }>>('/v1/admin/profiles');
  return data.map((profile) => ({
    id: profile.id,
    first_name: profile.first_name,
    last_name: profile.last_name,
    email: profile.email,
    whatsapp_number: profile.whatsapp_number,
  }));
}

/**
 * Fetch all users with roles for admin user management
 */
export async function getAllUsersWithRoles(): Promise<Array<any>> {
  const data = await platformApiFetch<Array<any>>('/v1/admin/users-with-roles');
  return (data || []).map(user => ({
    ...user,
    isAdmin: user.is_admin,
    isActive: user.is_active
  }));
}

/**
 * Get user profile by ID for admin purposes
 */
export async function getUserProfileById(userId: string): Promise<{
  first_name: string;
  last_name: string;
  email: string;
  whatsapp_number: string | null;
} | null> {
  try {
    return await platformApiFetch<{
      first_name: string;
      last_name: string;
      email: string;
      whatsapp_number: string | null;
    } | null>(`/v1/profiles/${userId}/basic`);
  } catch (error) {
    console.error("Error fetching user profile:", error);
    return null;
  }
}
